import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';

import Hero from '../components/HeroSection/Hero';
import CustomInput from '../components/Inputs/CustomInput';
import CustomTextarea from '../components/Inputs/CustomTextarea';
import SubmitButton from '../components/Buttons/SubmitButton';
import CustomSelect from '../components/Inputs/CustomSelect';
import useTiposIdentificacion from '../hooks/parameter/useTiposIdentificacion';
import { createPqr } from '../services/pqr.service';
import CustomFileInput from '../components/Inputs/CustomFileInput';
import AlertDialog from '../components/Alertas/AlertDialog';
import AlertError from '../components/Alertas/AlertError';
import useThemes from '../hooks/useThemes.js';
import { useApi } from '../hooks/useApi';

const schema = yup
  .object({
    tipoDocumento: yup.string().required('El tipo de documento es requerido'),
    DocNumero: yup
      .number()
      .typeError('Ingrese un dato válido')
      .integer('Ingrese solo números enteros')
      .positive('El número de documento debe ser positivo')
      .required('El número de documento es requerido'),
    Nombres: yup.string().required('Los nombres son requeridos'),
    Apellidos: yup.string().required('Los apellidos son requeridos'),
    sede: yup.string().required('La sede es requerida'),
    telefono: yup
      .number()
      .typeError('El teléfono debe ser un número')
      .positive('El número de teléfono debe ser positivo')
      .integer('Ingrese solo números enteros')
      .min(1000000, 'Ingrese mínimo 7 dígitos')
      .required('El teléfono es requerido'),
    Email: yup
      .string()
      .email('Ingrese un correo válido')
      .required('El correo electrónico es requerido'),
    Asunto: yup.string().required('El asunto es requerido'),
    Comentario: yup.string().required('El comentario es requerido'),
  })
  .required();

const PQR = () => {
  const { sedes, getSedes } = useApi();
  const { tiposIdentificacion } = useTiposIdentificacion();
  const history = useHistory();
  const { backgroundWhite } = useThemes();

  const [alertDialogProps, setAlertDialogProps] = useState({ isOpen: false });
  const [alertErrorProps, setAlertErrorProps] = useState({ isOpen: false });
  const [isLoading, setIsLoading] = useState(false);

  const { handleSubmit, control, reset } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setIsLoading(true);

    const sendData = {
      ...data,
      Sede: JSON.parse(data.sede).id,
      DocTipo: JSON.parse(data.tipoDocumento).id,
    };

    try {
      const response = await createPqr(sendData);

      if (response.data.code === 201 || response.data.code === 200) {
        setAlertDialogProps({
          isOpen: true,
          onClose: () => setAlertDialogProps({ isOpen: false }),
          primaryOnClick: () => history.push('/'),
          secondaryOnClick: () => setAlertDialogProps({ isOpen: false }),
        });
      }

      setIsLoading(false);

      reset({
        tipoDocumento: '',
        DocNumero: '',
        Nombres: '',
        Apellidos: '',
        sede: '',
        telefono: '',
        Email: '',
        Asunto: '',
        Comentario: '',
        Adjuntos: '',
      });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setAlertErrorProps({
        titulo: error.message,
        isOpen: true,
        onClose: () => setAlertDialogProps({ isOpen: false }),
        primaryOnClick: () => history.push('/'),
        secondaryOnClick: () => setAlertDialogProps({ isOpen: false }),
      });
    }
  };

  useEffect(() => {
    getSedes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <Hero
        title='Peticiones'
        subtitle='resuelve tus preguntas, quejas y reclamos'
        color={backgroundWhite}
        imgSrc='img/Servicios/consultas-quejas-reclamos.jpg'
      />

      <AlertDialog {...alertDialogProps} />
      <AlertError {...alertErrorProps} />

      <FormContainer
        onSubmit={handleSubmit(onSubmit)}
        background={backgroundWhite}
      >
        <InputContainer>
          <Controller
            name='tipoDocumento'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomSelect
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='Tipo de documento *'
                width='49%'
                error={error}
                data={tiposIdentificacion.map((item) => {
                  return { name: item.docName, value: JSON.stringify(item) };
                })}
              />
            )}
          />

          <Controller
            name='DocNumero'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='Número de documento *'
                width='49%'
                error={error}
                type='number'
              />
            )}
          />
        </InputContainer>

        <InputContainer>
          <Controller
            name='Nombres'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='Nombres *'
                width='49%'
                error={error}
              />
            )}
          />

          <Controller
            name='Apellidos'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='Apellidos *'
                width='49%'
                error={error}
              />
            )}
          />
        </InputContainer>

        <InputContainer>
          <Controller
            name='sede'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomSelect
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                error={error}
                label='Sede *'
                data={sedes.data.map((item) => {
                  return {
                    value: JSON.stringify(item),
                    name: item.nombre,
                  };
                })}
              />
            )}
          />
        </InputContainer>

        <InputContainer>
          <Controller
            name='telefono'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='teléfono *'
                width='49%'
                error={error}
              />
            )}
          />

          <Controller
            name='Email'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='correo electrónico *'
                width='49%'
                error={error}
                type='email'
              />
            )}
          />
        </InputContainer>

        <InputContainer>
          <Controller
            name='Asunto'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='Asunto *'
                error={error}
              />
            )}
          />
        </InputContainer>

        <InputContainer>
          <Controller
            name='Adjuntos'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomFileInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='Adjuntar soporte'
                error={error}
              />
            )}
          />
        </InputContainer>

        <InputContainer>
          <Controller
            name='Comentario'
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomTextarea
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label='Comentario *'
                error={error}
              />
            )}
          />
        </InputContainer>

        <SubmitButton label='Enviar' isLoading={isLoading} />
      </FormContainer>
    </Container>
  );
};

export default PQR;

// ##### Estilos #####
const Container = styled.div`
  position: relative;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.background};
  margin-top: 5vh;
  margin-bottom: 0;
`;

const InputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 50vw;

  @media (max-width: 1023px) {
    flex-wrap: wrap;
    width: 90vw;
  }
`;
